<<template>
  <v-app class="pc-app">
    <!-- ═══════════════════════════════════════
         INTRO ANIMATION (conservée, améliorée)
         ═══════════════════════════════════════ -->
    <transition name="fade-slow">
      <div v-if="showIntro" class="intro-shell">
        <div class="intro-particles">
          <span v-for="n in 12" :key="n" class="particle" :style="particleStyle(n)" />
        </div>
        <div class="intro-center">
          <div class="logo-glow">
            <div class="logo-ring">
              <img src="/favicon.ico" alt="PettyCash" width="56" height="56" />
            </div>
          </div>
          <div class="brand-reveal">
            <span class="brand-petty">Petty</span>
            <span class="brand-cash">Cash</span>
          </div>
          <div class="loading-track">
            <div class="loading-fill" />
          </div>
        </div>
      </div>
    </transition>

    <!-- ═══════════════════════════════════════
         MAIN APPLICATION
         ═══════════════════════════════════════ -->
    <div v-show="!showIntro" class="app-shell">
      <!-- Ambient Background -->
      <div class="ambient-layer">
        <div class="orb orb-blue" />
        <div class="orb orb-teal" />
        <div class="orb orb-gold" />
      </div>

      <!-- HERO SECTION -->
      <section class="hero-zone">
        <div class="hero-content max-w">
          <div class="hero-badge animate-enter">
            <v-icon small color="white">mdi-shield-check</v-icon>
            <span>Licensed FinTech Company</span>
          </div>
            <h1 class="hero-headline animate-enter" style="animation-delay: .1s">
              Welcome to<br />
              <span class="brand-hero">
                <span class="petty-blue">Petty</span><span class="cash-green">Cash</span>
                <span class="sas-gold"> SAS</span>
              </span>
            </h1>
          <p class="hero-lead animate-enter" style="animation-delay: .2s">
            Secure savings, smart investments, and seamless money management — all in one place.
          </p>
          <div class="hero-ctas animate-enter" style="animation-delay: .3s">
            <button class="btn-primary-glow" @click="scrollToAccounts">
              <span>Open Account</span>
              <v-icon small color="white">mdi-arrow-down</v-icon>
            </button>
            <button class="btn-ghost" @click="goToMarketplace">
              <v-icon small color="primary">mdi-trending-up</v-icon>
              <span>Explore Marketplace</span>
            </button>
          </div>
        </div>

        <!-- Floating Stats (hero overlay) -->
        <div class="hero-float-stats max-w animate-enter" style="animation-delay: .5s">
          <div class="hstat-card" v-for="(stat, i) in heroStats" :key="i">
            <div class="hstat-icon">
              <v-icon :color="stat.color" size="28">{{ stat.icon }}</v-icon>
            </div>
            <div class="hstat-body">
              <div class="hstat-value">{{ stat.value }}</div>
              <div class="hstat-label">{{ stat.label }}</div>
            </div>
          </div>
        </div>
      </section>

      <!-- ACCOUNT CARDS (Progressive Disclosure) -->
      <section class="accounts-zone" id="accounts-anchor" ref="accountsAnchor">
        <div class="max-w">
          <header class="section-header scroll-reveal">
            <a href="https://pettycashglobal.com/" class="section-eyebrow">About Us</a>
            <h2 class="section-title">Choose your account</h2>
            <p class="section-sub">Pick what fits your goals. Upgrade or switch anytime.</p>
          </header>

          <!-- Mobile: Horizontal Scroll | Desktop: Grid -->
          <div class="accounts-grid">
            <div
              v-for="(account, idx) in accounts"
              :key="idx"
              class="account-card"
              :class="{ 'is-expanded': expandedCard === idx, [`card-accent-${account.theme}`]: true }"
              :style="{ transitionDelay: `${idx * 0.1}s` }"
            >
              <div class="card-glow" />
              <div class="card-inner">
                <div class="card-top">
                  <div class="card-icon-wrap" :class="`bg-${account.theme}`">
                    <v-icon :color="account.theme === 'blue' ? 'white' : account.theme" size="32">
                      {{ account.icon }}
                    </v-icon>
                  </div>
                  <div class="card-badge" :class="`badge-${account.theme}`">{{ account.badge }}</div>
                </div>

                <h3 class="card-title">{{ account.title }}</h3>
                <div class="card-price" :class="`text-${account.theme}`">{{ account.price }}</div>
                <p class="card-teaser">{{ account.teaser }}</p>

                <!-- Expandable details (progressive disclosure) -->
                <!-- REMPLACER tout le bloc transition+card-details par ceci : -->
                <div v-show="expandedCard === idx" class="card-details">
                  <ul class="feature-list">
                    <li v-for="(feat, fidx) in account.features" :key="fidx">
                      <v-icon small :color="account.theme === 'blue' ? 'primary' : account.theme">
                        mdi-check-circle
                      </v-icon>
                      <span>{{ feat }}</span>
                    </li>
                  </ul>
                </div>


                <div class="card-actions">
                  <v-btn
                    block
                    :color="account.theme === 'blue' ? 'primary' : account.theme"
                    dark
                    class="card-cta"
                    elevation="0"
                    @click="account.action"
                  >
                    {{ account.cta }}
                    <v-icon right small>mdi-arrow-right</v-icon>
                  </v-btn>
                  <button class="details-toggle" @click.stop="toggleCard(idx)">
                    {{ expandedCard === idx ? 'Less details' : 'More details' }}
                    <v-icon x-small>{{ expandedCard === idx ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Quick Chips (secondary navigation) -->
          <div class="quick-chips scroll-reveal">
            <v-chip
              v-for="(chip, cidx) in quickChips"
              :key="cidx"
              :color="chip.color"
              text-color="white"
              class="quick-chip"
              @click="chip.action"
            >
              <v-icon left small>{{ chip.icon }}</v-icon>
              {{ chip.label }}
            </v-chip>
          </div>
        </div>
      </section>

      <!-- WHY PETTYCASH (Bento Grid) -->
      <section class="bento-zone">
        <div class="max-w">
          <header class="section-header scroll-reveal">
            <span class="section-eyebrow">Why us</span>
            <h2 class="section-title">Built for trust & speed</h2>
          </header>

          <div class="bento-grid">
            <div class="bento-card bento-large scroll-reveal" style="transition-delay: 0s">
              <div class="bento-icon bg-soft-primary">
                <v-icon size="40" color="primary">mdi-chart-line</v-icon>
              </div>
              <h3>Smart Analytics</h3>
              <p>Real-time insights into your spending, savings, and investment performance. Make data-driven decisions.</p>
              <div class="bento-visual">
                <div class="mock-bar" v-for="b in 7" :key="b" :style="{ height: `${20 + Math.random() * 60}%` }" />
              </div>
            </div>

            <div class="bento-card scroll-reveal" style="transition-delay: .1s">
              <div class="bento-icon bg-soft-teal">
                <v-icon size="32" color="teal">mdi-headset</v-icon>
              </div>
              <h3>24/7 Support</h3>
              <p>Our team is always online when you need us.</p>
            </div>

            <div class="bento-card scroll-reveal" style="transition-delay: .2s">
              <div class="bento-icon bg-soft-purple">
                <v-icon size="32" color="purple">mdi-flash</v-icon>
              </div>
              <h3>Instant Transfers</h3>
              <p>Send & receive money in seconds, not days.</p>
            </div>

            <div class="bento-card bento-wide scroll-reveal" style="transition-delay: .3s">
              <div class="bento-icon bg-soft-orange">
                <v-icon size="32" color="orange">mdi-shield-check</v-icon>
              </div>
              <h3>Bank-Grade Security</h3>
              <p>99.9% uptime with end-to-end encryption and multi-factor authentication protecting every transaction.</p>
            </div>
          </div>
        </div>
      </section>

      <!-- TRUST STRIP -->
      <section class="trust-zone">
        <div class="max-w trust-flex scroll-reveal">
          <div class="trust-block">
            <div class="trust-pulse">
              <v-icon size="36" color="primary">mdi-account-group</v-icon>
            </div>
            <div class="trust-num" ref="counterUsers">{{ statsData.totalUsers || '0' }}+</div>
            <div class="trust-label">Active Users</div>
          </div>
          <div class="trust-divider" />
          <div class="trust-block">
            <div class="trust-pulse">
              <v-icon size="36" color="teal">mdi-bulletin-board</v-icon>
            </div>
            <div class="trust-num" ref="counterCampaigns">{{ statsData.totalCampaigns || '0' }}+</div>
            <div class="trust-label">Campaigns</div>
          </div>
          <div class="trust-divider" />
          <div class="trust-block">
            <div class="trust-pulse">
              <v-icon size="36" color="orange">mdi-server</v-icon>
            </div>
            <div class="trust-num">99.9%</div>
            <div class="trust-label">Uptime</div>
          </div>
        </div>
      </section>

      <!-- FOOTER -->
      <footer class="pc-footer">
        <div class="max-w">
          <div class="footer-grid">
            <div class="footer-brand-block">
              <div class="footer-logo">
                <img src="/favicon.ico" width="28" height="28" alt="" />
                <span class="brand-petty">Petty</span><span class="brand-cash">Cash</span>
                <span class="sas-tag">SAS</span>
              </div>
              <p class="footer-mission">Your trusted FinTech partner for secure savings and smart investments.</p>
              <div class="footer-socials">
                <v-btn icon small class="social-btn"><v-icon small>mdi-facebook</v-icon></v-btn>
                <v-btn icon small class="social-btn"><v-icon small>mdi-twitter</v-icon></v-btn>
                <v-btn icon small class="social-btn"><v-icon small>mdi-linkedin</v-icon></v-btn>
                <v-btn icon small class="social-btn"><v-icon small>mdi-instagram</v-icon></v-btn>
              </div>
            </div>

            <div class="footer-col">
              <h4>Company</h4>
              <a href="https://pettycashglobal.com/">About Us</a>
              <a href="#">Contact</a>
            </div>
            <div class="footer-col">
              <h4>Legal</h4>
              <a href="#">Privacy</a>
              <a href="#">Terms</a>
              <a href="#">Security</a>
              <a href="#">Compliance</a>
            </div>
            <div class="footer-col">
              <h4>Support</h4>
              <a href="#">Help Center</a>
              <a href="#">FAQ</a>
              <a href="#">Community</a>
              <a href="#">Docs</a>
            </div>
          </div>

          <div class="footer-bottom">
            <span>© 2026 PettyCash SAS. All rights reserved.</span>
            <div class="footer-badges">
              <span><v-icon x-small>mdi-shield-check</v-icon> Secure</span>
              <span><v-icon x-small>mdi-bank</v-icon> Licensed</span>
              <span><v-icon x-small>mdi-finance</v-icon> FinTech</span>
            </div>
          </div>
        </div>
      </footer>

      <!-- ═══════════════════════════════════════
           CHATBOT (Logic preserved, UI modernized & responsive)
           ═══════════════════════════════════════ -->
      <!-- Mobile Chat Overlay (Full/Drawer without touch blocking) -->
      <transition name="chat-slide-up">
        <div v-if="isMobile && chatOpen" class="chat-mobile-overlay" @click.self="chatOpen = false">
          <div class="chat-card-mobile">
            <div class="chat-header-glass">
              <div class="chat-identity">
                <div class="chat-avatar-pulse">
                  <v-icon color="white" size="20">mdi-robot</v-icon>
                </div>
                <div>
                  <div class="chat-name">PettyCash AI</div>
                  <div class="chat-status">
                    <span class="status-dot" />
                    {{ isTyping ? 'Typing...' : 'Online' }}
                  </div>
                </div>
              </div>
              <button class="chat-close-btn" @click="chatOpen = false" aria-label="Close">
                <v-icon color="white" size="22">mdi-close</v-icon>
              </button>
            </div>

            <!-- Messages area with native touch scroll -->
            <div class="chat-body-mobile" ref="chatMessages">
              <div
                v-for="(msg, idx) in chatMessages"
                :key="idx"
                :class="['msg-row', msg.type === 'user' ? 'msg-user' : 'msg-bot']"
              >
                <div v-if="msg.type === 'bot'" class="msg-avatar">
                  <v-icon size="16" color="primary">mdi-robot</v-icon>
                </div>
                <div class="msg-content-column">
                  <div class="msg-bubble" :class="msg.type"><div v-if="msg.type === 'bot'" v-html="formatBotMessage(msg.text)" class="bot-md" /><template v-else>{{ msg.text }}</template></div>

                  <!-- Suggested video cards if any -->
                  <div
                    v-if="msg.suggested_videos && msg.suggested_videos.length"
                    class="msg-video-suggestions"
                  >
                    <div
                      v-for="vItem in msg.suggested_videos"
                      :key="vItem.id"
                      class="video-suggestion-card"
                      @click="openVideoModal(vItem)"
                    >
                      <div class="video-card-thumb">
                        <img
                          v-if="vItem.thumbnail_url"
                          :src="vItem.thumbnail_url"
                          alt="Thumbnail"
                          class="thumb-img"
                        />
                        <div v-else class="thumb-fallback">
                          <v-icon color="white" size="20">mdi-play-circle</v-icon>
                        </div>
                        <div class="play-badge">
                          <v-icon color="white" size="12">mdi-play</v-icon>
                        </div>
                      </div>
                      <div class="video-card-info">
                        <div class="video-card-badge">🎬 Video Guide</div>
                        <div class="video-card-title">{{ vItem.title }}</div>
                        <div class="video-watch-link">
                          <span>Watch video</span>
                          <v-icon size="12" color="primary">mdi-arrow-right</v-icon>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="isTyping" class="msg-row msg-bot">
                <div class="msg-avatar"><v-icon size="16" color="primary">mdi-robot</v-icon></div>
                <div class="msg-bubble bot typing">
                  <span class="t-dot" /><span class="t-dot" /><span class="t-dot" />
                </div>
              </div>
            </div>

            <!-- Input area -->
            <div class="chat-input-area">
              <div class="chat-input-wrapper">
                <input
                  type="text"
                  v-model="userInput"
                  @keypress.enter="handleChatSubmit"
                  placeholder="Ask anything / Posez votre question..."
                  :disabled="isTyping"
                  class="chat-native-input"
                />
                <button
                  class="chat-send-btn"
                  @click="handleChatSubmit"
                  :disabled="!userInput.trim() || isTyping"
                >
                  <v-icon size="18" color="white">mdi-send</v-icon>
                </button>
              </div>
            </div>
          </div>
        </div>
      </transition>

      <!-- Desktop Dialog -->
      <v-dialog v-model="chatOpen" v-if="!isMobile" width="420" content-class="chat-dialog">
        <v-card class="chat-card-desk">
          <div class="chat-header-glass">
            <div class="chat-identity">
              <div class="chat-avatar-pulse">
                <v-icon color="white" size="20">mdi-robot</v-icon>
              </div>
              <div>
                <div class="chat-name">PettyCash AI Assistant</div>
                <div class="chat-status">
                  <span class="status-dot" />
                  {{ isTyping ? 'Typing...' : 'Online' }}
                </div>
              </div>
            </div>
            <v-btn icon small @click="chatOpen = false" color="white"><v-icon>mdi-close</v-icon></v-btn>
          </div>
          <div class="chat-body-desk" ref="chatMessagesDesktop">
            <div
              v-for="(msg, idx) in chatMessages"
              :key="idx"
              :class="['msg-row', msg.type === 'user' ? 'msg-user' : 'msg-bot']"
            >
              <div v-if="msg.type === 'bot'" class="msg-avatar">
                <v-icon size="14" color="primary">mdi-robot</v-icon>
              </div>
              <div class="msg-content-column">
                <div class="msg-bubble" :class="msg.type"><div v-if="msg.type === 'bot'" v-html="formatBotMessage(msg.text)" class="bot-md" /><template v-else>{{ msg.text }}</template></div>

                <!-- Suggested video cards if any -->
                <div
                  v-if="msg.suggested_videos && msg.suggested_videos.length"
                  class="msg-video-suggestions"
                >
                  <div
                    v-for="vItem in msg.suggested_videos"
                    :key="vItem.id"
                    class="video-suggestion-card"
                    @click="openVideoModal(vItem)"
                  >
                    <div class="video-card-thumb">
                      <img
                        v-if="vItem.thumbnail_url"
                        :src="vItem.thumbnail_url"
                        alt="Thumbnail"
                        class="thumb-img"
                      />
                      <div v-else class="thumb-fallback">
                        <v-icon color="white" size="20">mdi-play-circle</v-icon>
                      </div>
                      <div class="play-badge">
                        <v-icon color="white" size="12">mdi-play</v-icon>
                      </div>
                    </div>
                    <div class="video-card-info">
                      <div class="video-card-badge">🎬 Video Guide</div>
                      <div class="video-card-title">{{ vItem.title }}</div>
                      <div class="video-watch-link">
                        <span>Watch video</span>
                        <v-icon size="12" color="primary">mdi-arrow-right</v-icon>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="isTyping" class="msg-row msg-bot">
              <div class="msg-avatar"><v-icon size="14" color="primary">mdi-robot</v-icon></div>
              <div class="msg-bubble bot typing">
                <span class="t-dot" /><span class="t-dot" /><span class="t-dot" />
              </div>
            </div>
          </div>
          <div class="chat-input-area">
            <v-text-field
              v-model="userInput"
              @keypress.enter="handleChatSubmit"
              placeholder="Ask anything / Posez votre question..."
              solo
              flat
              dense
              hide-details
              :disabled="isTyping"
              class="chat-field"
            >
              <template v-slot:append>
                <v-btn
                  icon
                  small
                  color="primary"
                  @click="handleChatSubmit"
                  :disabled="!userInput.trim() || isTyping"
                >
                  <v-icon>mdi-send</v-icon>
                </v-btn>
              </template>
            </v-text-field>
          </div>
        </v-card>
      </v-dialog>

      <!-- ═══════════════════════════════════════
           VIDEO PLAYER MODAL DIALOG
           ═══════════════════════════════════════ -->
      <v-dialog
        v-model="videoModal"
        max-width="620"
        content-class="chat-video-dialog"
        :fullscreen="$vuetify.breakpoint.xsOnly"
        persistent
      >
        <v-card v-if="activeVideo" class="rounded-xl overflow-hidden bg-dark-video">
          <div class="video-dialog-header d-flex align-center justify-space-between px-4 py-3">
            <div class="d-flex align-center text-truncate me-2">
              <v-icon color="primary" class="me-2" size="20">mdi-play-circle</v-icon>
              <span class="text-subtitle-2 font-weight-bold text-truncate white--text">
                {{ activeVideo.title }}
              </span>
            </div>
            <button class="modal-close-icon" @click="closeVideoModal" aria-label="Close">
              <v-icon color="white" size="20">mdi-close</v-icon>
            </button>
          </div>

          <div class="video-dialog-player bg-black">
            <iframe
              v-if="isEmbedUrl(activeVideo.video_url)"
              :src="getEmbedUrl(activeVideo.video_url)"
              frameborder="0"
              allowfullscreen
              class="video-frame-modal"
            ></iframe>
            <video
              v-else-if="activeVideo.video_url"
              :src="activeVideo.video_url"
              controls
              autoplay
              class="video-element-modal"
            ></video>
            <div v-else class="pa-8 text-center white--text">
              <v-icon size="40" color="grey">mdi-video-off</v-icon>
              <div class="mt-2 text-caption">No video source available</div>
            </div>
          </div>

          <div v-if="activeVideo.description" class="px-4 py-3 bg-dark-caption text-caption grey--text text--lighten-1">
            {{ activeVideo.description }}
          </div>
        </v-card>
      </v-dialog>

      <!-- Floating Chat Button & Invitation Bubble -->
      <div class="chat-fab-wrapper" :class="{ 'fab-hidden': chatOpen }">
        <!-- Invitation speech bubble before opening chat (English predominant) -->
        <transition name="bubble-slide">
          <div
            v-if="showChatBubble && !chatOpen"
            class="chat-invitation-bubble"
            @click="chatOpen = true"
          >
            <div class="bubble-header">
              <div class="bubble-avatar-mini">
                <v-icon x-small color="white">mdi-headset</v-icon>
              </div>
              <span class="bubble-title">PettyCash Support</span>
              <button class="bubble-close-btn" @click.stop="showChatBubble = false" title="Close">✕</button>
            </div>
            <p class="bubble-text">👋 How can I help you today?</p>
            <span class="bubble-subtext">Comment puis-je vous aider ?</span>
          </div>
        </transition>

        <!-- Main FAB Button -->
        <v-btn
          fab
          color="primary"
          class="chat-fab"
          @click="chatOpen = true"
          elevation="8"
        >
          <v-badge v-if="unreadMessages > 0" :content="unreadMessages" color="red" overlap>
            <v-icon :size="isMobile ? 24 : 28" color="white">mdi-chat</v-icon>
          </v-badge>
          <v-icon v-else :size="isMobile ? 24 : 28" color="white">mdi-chat</v-icon>
        </v-btn>
      </div>
    </div>
  </v-app>
</template>

<script>
import axios from "axios";
import referralMixin from '@/mixins/referralMixin';
import ReferralField from '@/components/ReferralField.vue';

export default {
  name: 'FintechHomePage',
  mixins: [referralMixin],
  components: { ReferralField },

  data() {
    return {
      showIntro: true,
      chatOpen: false,
      showChatBubble: true,
      unreadMessages: 0,
      videoModal: false,
      activeVideo: null,
      chatMessages: [
        {
          type: 'bot',
          text: "Hello! 👋 I'm your PettyCash AI assistant. How can I help you today?\n\n(Bonjour ! 👋 Je suis l'assistant virtuel PettyCash. Comment puis-je vous aider ?)"
        }
      ],
      userInput: '',
      isTyping: false,
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,
      statsData: { totalUsers: 0, totalCampaigns: 0 },
      conversationHistory: [],
      expandedCard: null,
      cachedVideos: [],
      videosLoaded: false,
    }
  },

  computed: {
    isMobile() { return this.windowWidth < 768 },
    isTablet() { return this.windowWidth >= 768 && this.windowWidth < 1024 },
    iconSize() {
      if (this.isMobile) return 28
      if (this.isTablet) return 30
      return 32
    },
    heroStats() {
      return [
        { icon: 'mdi-account-group', color: 'primary', value: (this.statsData.totalUsers || '10K') + '+', label: 'Users' },
        { icon: 'mdi-bulletin-board', color: 'teal', value: (this.statsData.totalCampaigns || '500') + '+', label: 'Campaigns' },
        { icon: 'mdi-shield-check', color: 'orange', value: '99.9%', label: 'Uptime' },
      ]
    },
    accounts() {
      return [
        {
          title: 'Marketplace',
          badge: 'Popular',
          price: 'Free Access',
          teaser: 'Discover financial products & investment opportunities tailored to your goals.',
          features: ['Investment Options', 'Financial Products', 'Campaign Browsing', 'Real-time Rates'],
          icon: 'mdi-trending-up',
          theme: 'blue',
          cta: 'Explore Marketplace',
          action: this.goToMarketplace
        },
        {
          title: 'Basic Account',
          badge: 'Recommended',
          price: 'Free Forever',
          teaser: 'Save, track transactions, and manage your personal finances with ease.',
          features: ['Savings Account', 'Transaction Tracking', 'Monthly Reports', 'Secure Vault'],
          icon: 'mdi-account',
          theme: 'teal',
          cta: 'Create Basic Account',
          action: this.goToBasicAccount
        },
        {
          title: 'Business Suite',
          badge: 'Enterprise',
          price: '10,500 FCFA',
          teaser: 'All-in-one tools to launch and scale your business with multi-user access.',
          features: ['Advanced Business Tools', 'Multi-user Access', 'Earn up to 55% commission', 'Priority Support'],
          icon: 'mdi-briefcase',
          theme: 'orange',
          cta: 'Unlock Business Suite',
          action: this.goToBusinessAccount
        }
      ]
    },
    quickChips() {
      return [
        { label: 'Marketplace', icon: 'mdi-trending-up', color: 'primary', action: this.goToMarketplace },
        { label: 'Basic Account', icon: 'mdi-account', color: 'teal', action: this.goToBasicAccount },
        { label: 'Business Suite', icon: 'mdi-briefcase', color: 'orange', action: this.goToBusinessAccount },
      ]
    }
  },

  mounted() {
    this.setFavicon()
    this.fetchStats()
    this.initScrollReveal()
    this.preloadVideos()
    setTimeout(() => { this.showIntro = false }, 2200)
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.handleResize)
    }
  },

  beforeDestroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.handleResize)
    }
    if (typeof document !== 'undefined') {
      document.body.classList.remove('chat-open-locked')
    }
  },

  methods: {
    particleStyle(n) {
      const top = Math.random() * 100
      const left = Math.random() * 100
      const delay = Math.random() * 2
      const duration = 3 + Math.random() * 4
      return {
        top: `${top}%`,
        left: `${left}%`,
        animationDelay: `${delay}s`,
        animationDuration: `${duration}s`
      }
    },

    toggleCard(idx) {
      this.expandedCard = this.expandedCard === idx ? null : idx
    },

    scrollToAccounts() {
      // Sur mobile, cible la carte Basic Account (index 1) directement
      // Sur desktop les 3 cartes sont visibles donc l'anchor suffit
      if (this.isMobile) {
        const cards = document.querySelectorAll('.account-card')
        const basicCard = cards[1]
        if (basicCard) basicCard.scrollIntoView({ behavior: 'smooth', block: 'start' })
      } else {
        const el = document.getElementById('accounts-anchor')
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },

    initScrollReveal() {
      if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible')
            observer.unobserve(entry.target)
          }
        })
      }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' })
      this.$nextTick(() => {
        document.querySelectorAll('.scroll-reveal').forEach(el => observer.observe(el))
      })
    },

    setFavicon() {
      const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
          <defs>
            <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color:#00479B;stop-opacity:1" />
              <stop offset="100%" style="stop-color:#009688;stop-opacity:1" />
            </linearGradient>
          </defs>
          <circle cx="50" cy="50" r="48" fill="url(#grad)"/>
          <text x="50" y="65" font-family="Arial, sans-serif" font-size="50" font-weight="bold" fill="white" text-anchor="middle">PC</text>
        </svg>
      `
      const blob = new Blob([svg], { type: 'image/svg+xml' })
      const url = URL.createObjectURL(blob)
      let link = document.querySelector("link[rel~='icon']")
      if (!link) {
        link = document.createElement('link')
        link.rel = 'icon'
        document.head.appendChild(link)
      }
      link.href = url
      document.title = 'PettyCash - Your Financial Partner'
    },

    async fetchStats() {
      try {
        const usersResponse = await axios.get('/api/v1/home/users/count/')
        this.statsData.totalUsers = usersResponse.data.count || 0
        const campaignsResponse = await axios.get('/api/v1/home/campaigns/count/')
        this.statsData.totalCampaigns = campaignsResponse.data.count || 0
      } catch (error) {
        console.error('Error fetching stats:', error)
        this.statsData.totalUsers = '10K'
        this.statsData.totalCampaigns = '500'
      }
    },

    handleResize() {
      this.windowWidth = window.innerWidth
    },

    async handleChatSubmit() {
      if (!this.userInput.trim() || this.isTyping) return
      const userMessage = this.userInput.trim()
      this.chatMessages.push({ type: 'user', text: userMessage })
      this.userInput = ''
      this.isTyping = true
      this.conversationHistory.push({ role: 'user', content: userMessage })
      this.$nextTick(() => this.scrollToBottom())

      try {
        const response = await axios.post('/api/v1/home/chat/ai-assistant/', {
          message: userMessage,
          conversation_history: this.conversationHistory
        })
        const botResponse = response.data.response || "I'm sorry, I couldn't process that. Please try again."
        const suggestedVideos = this.mergeSuggestions(
          response.data.suggested_videos || [],
          this.getLocalVideoSuggestions(userMessage)
        )

        this.conversationHistory.push({ role: 'assistant', content: botResponse })
        this.chatMessages.push({
          type: 'bot',
          text: botResponse,
          suggested_videos: suggestedVideos
        })
        if (!this.chatOpen) this.unreadMessages++
      } catch (error) {
        console.error('Chat error:', error)
        this.chatMessages.push({
          type: 'bot',
          text: 'I apologize, but I\'m having trouble connecting right now. Please try again in a moment.'
        })
      } finally {
        this.isTyping = false
        this.$nextTick(() => this.scrollToBottom())
      }
    },

    openVideoModal(video) {
      this.activeVideo = video
      // Toujours fermer le chat : la video doit etre au premier plan
      this.chatOpen = false
      this.videoModal = true
    },

    async preloadVideos() {
      if (this.videosLoaded) return
      try {
        const res = await axios.get('/api/v1/home/chatbot-videos/?is_active=true')
        this.cachedVideos = res.data.videos || []
      } catch (e) {
        // Suggestion locale optionnelle : on ne bloque jamais le chat en cas d'erreur
      }
      this.videosLoaded = true
    },

    _foldAccents(text) {
      return (text || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
    },

    _meaningfulTokens(text) {
      const stop = new Set([
        'pour', 'avec', 'dans', 'une', 'des', 'les', 'sur', 'par', 'pas', 'plus', 'moins',
        'comment', 'quoi', 'quand', 'est', 'aussi', 'votre', 'vos', 'vous', 'nous', 'mais',
        'tres', 'the', 'and', 'for', 'with', 'from', 'that', 'this', 'how', 'what', 'why',
        'when', 'are', 'can', 'should', 'please', 'help', 'want', 'avez', 'pouvez', 'peut',
        'fait', 'faire', 'voir', 'cherche', 'veux', 'besoin', 'about', 'also', 'like',
        'need', 'bien', 'bonjour', 'bonsoir', 'salut', 'hello', 'merci', 'thanks', 'thank',
        'moi', 'tout', 'tous', 'mon', 'ma', 'mes', 'oui', 'non', 'ok', 'yes', 'no', 'get',
      ])
      return new Set(
        this._foldAccents(text)
          .split(/[^a-z0-9]+/i)
          .filter((w) => w.length >= 3 && !stop.has(w))
      )
    },

    getLocalVideoSuggestions(message) {
      if (!this.cachedVideos.length) return []
      const userTokens = this._meaningfulTokens(message)
      if (!userTokens.size) return []

      const catAliases = {
        savings: ['epargne', 'epargner', 'economie', 'economiser', 'save', 'saving', 'goal',
          'objectif', 'campagne', 'campaign', 'discipline', 'interet', 'budget'],
        njangi: ['njangi', 'tontine', 'tontines', 'groupe', 'group', 'tour', 'rotation',
          'contribution', 'cotisation', 'caisse'],
        business: ['business', 'suite', 'commission', 'affiliate', 'affiliation', 'referral',
          'parrain', 'filleul', 'earn', 'gagner', 'partenaire', 'partner'],
        marketplace: ['marketplace', 'market', 'bnpl', 'credit', 'installment', 'acheter',
          'shop', 'produit', 'product', 'boutique', 'commande'],
        wallet: ['wallet', 'depot', 'deposit', 'retrait', 'withdraw', 'withdrawal', 'transfer',
          'transfert', 'orange', 'mtn', 'momo', 'solde', 'balance', 'recharge', 'topup'],
        account: ['compte', 'account', 'register', 'registration', 'inscription', 'signup',
          'login', 'connexion', 'connecter', 'ouvrir', 'otp', 'code'],
        general: ['pettycash', 'presentation', 'presentation', 'fondateur', 'founder',
          'decouvrir', 'discover', 'apropos', 'about', 'plateforme', 'platform'],
      }

      const scored = []
      for (const v of this.cachedVideos) {
        let s = 0
        const cat = ((v.category) || 'general').toLowerCase()
        const aliases = catAliases[cat] || []
        for (const a of aliases) {
          if (userTokens.has(a)) { s += 6; break }
        }

        for (const t of this._meaningfulTokens(v.title)) {
          if (userTokens.has(t)) s += 3
        }

        // Correlation entre la DESCRIPTION de la video et le besoin client
        for (const t of this._meaningfulTokens(v.description)) {
          if (userTokens.has(t)) s += 2
        }

        if (Array.isArray(v.keywords)) {
          for (const kw of v.keywords) {
            const phrase = this._foldAccents(kw).trim().replace(/\s+/g, ' ')
            if (phrase.length > 2 && this._foldAccents(message).includes(phrase)) s += 5
          }
        }

        if (s >= 4) scored.push({ v, s })
      }

      scored.sort((a, b) => b.s - a.s)
      return scored.slice(0, 2).map((x) => x.v)
    },

    mergeSuggestions(backend, local) {
      const merged = []
      const seen = new Set()
      ;(backend || []).concat(local || []).forEach((video) => {
        if (!video || seen.has(video.id)) return
        seen.add(video.id)
        merged.push(video)
      })
      return merged.slice(0, 2)
    },

    _escapeHtml(s) {
      return (s || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
    },

    _renderInline(s) {
      let out = s
      out = out.replace(/`([^`\n]+)`/g, '<code class="chat-code">$1</code>')
      out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      out = out.replace(/__([^_]+)__/g, '<strong>$1</strong>')
      out = out.replace(/(^|[^*\w])\*([^*\n]+)\*/g, '$1<em>$2</em>')
      out = out.replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>')
      out = out.replace(/(^|\s)(https?:\/\/[^\s<]+)/g, '$1<a href="$2" target="_blank" rel="noopener">$2</a>')
      return out
    },

    formatBotMessage(text) {
      if (!text) return ''
      const lines = this._escapeHtml(text).split('\n')
      let html = ''
      let inList = false
      const closeList = () => {
        if (inList) { html += '</ul>'; inList = false }
      }
      for (const rawLine of lines) {
        const line = rawLine.trim()
        if (!line) { closeList(); continue }
        const h = line.match(/^(#{1,4})\s+(.*)$/)
        if (h) {
          closeList()
          html += '<div class="chat-md-h' + h[1].length + '">' + this._renderInline(h[2]) + '</div>'
          continue
        }
        const li = line.match(/^[-*\u2022]\s+(.*)$/)
        if (li) {
          if (!inList) { html += '<ul class="chat-md-ul">'; inList = true }
          html += '<li>' + this._renderInline(li[1]) + '</li>'
          continue
        }
        const ol = line.match(/^\d+[.)]\s+(.*)$/)
        if (ol) {
          closeList()
          html += '<div class="chat-md-li">' + this._renderInline(line) + '</div>'
          continue
        }
        closeList()
        html += '<div class="chat-md-p">' + this._renderInline(line) + '</div>'
      }
      closeList()
      return html
    },

    closeVideoModal() {
      this.videoModal = false
      this.activeVideo = null
    },

    isEmbedUrl(url) {
      if (!url) return false
      return url.includes('youtube.com') || url.includes('youtu.be') || url.includes('vimeo.com')
    },

    getEmbedUrl(url) {
      if (!url) return ''
      if (url.includes('youtube.com/watch?v=')) {
        const videoId = url.split('v=')[1]?.split('&')[0]
        return `https://www.youtube.com/embed/${videoId}?autoplay=1`
      }
      if (url.includes('youtu.be/')) {
        const videoId = url.split('youtu.be/')[1]?.split('?')[0]
        return `https://www.youtube.com/embed/${videoId}?autoplay=1`
      }
      return url
    },

    scrollToBottom() {
      const chatMessages = this.isMobile ? this.$refs.chatMessages : this.$refs.chatMessagesDesktop
      if (chatMessages) chatMessages.scrollTop = chatMessages.scrollHeight
    },

    goToMarketplace() {
      this.$router.push({
        name: 'market_place.home',
        query: { ref: this.$route.query.ref || this.$route.query.referral || this.referralCode || '' }
      })
    },

    goToBasicAccount() {
      const ref = this.$route.query.ref || this.$route.query.referral || this.referralCode || ''
      this.$router.push({
        name: 'signup',
        query: { ref, referral: ref, affiliate_referral: ref }
      })
    },

    goToBusinessAccount() {
      const ref = this.$route.query.ref || this.$route.query.referral || this.referralCode || ''
      this.$router.push({
        name: 'create_business_account5',
        query: { ref }
      })
    }
  },

  watch: {
    chatOpen(newVal) {
      if (newVal) {
        this.unreadMessages = 0
        this.showChatBubble = false
        if (typeof document !== 'undefined' && this.isMobile) {
          document.body.classList.add('chat-open-locked')
        }
        this.$nextTick(() => this.scrollToBottom())
      } else {
        if (typeof document !== 'undefined') {
          document.body.classList.remove('chat-open-locked')
        }
      }
    }
  }
}
</script>

<style scoped>
/* ═══════════════════════════════════════
   DESIGN TOKENS & RESET
   ═══════════════════════════════════════ */
.pc-app {
  --pc-primary: #00479B;
  --pc-primary-light: #0066CC;
  --pc-teal: #009688;
  --pc-orange: #F97316;
  --pc-purple: #8B5CF6;
  --pc-bg: #F8FAFC;
  --pc-surface: #FFFFFF;
  --pc-text: #0F172A;
  --pc-muted: #64748B;
  --pc-border: #E2E8F0;
  --radius: 1.25rem;
  --shadow-sm: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  --shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
  --shadow-lg: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
  --shadow-glow: 0 0 40px -10px var(--pc-primary);
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  background: var(--pc-bg) !important;
  overflow-x: hidden;
}

.max-w {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.25rem;
}

/* ═══════════════════════════════════════
   INTRO ANIMATION
   ═══════════════════════════════════════ */
.intro-shell {
  position: fixed;
  inset: 0;
  background: linear-gradient(135deg, #00479B 0%, #002a5c 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  overflow: hidden;
}

.intro-particles {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.particle {
  position: absolute;
  width: 6px;
  height: 6px;
  background: rgba(255,255,255,0.25);
  border-radius: 50%;
  animation: floatParticle linear infinite;
}

@keyframes floatParticle {
  0% { transform: translateY(0) scale(1); opacity: 0; }
  20% { opacity: 1; }
  80% { opacity: 1; }
  100% { transform: translateY(-100px) scale(0); opacity: 0; }
}

.intro-center {
  text-align: center;
  z-index: 2;
}

.logo-glow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  animation: popIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.logo-ring {
  width: 88px;
  height: 88px;
  border-radius: 50%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 0 rgba(255,255,255,0.4);
  animation: ringPulse 2s infinite;
}

@keyframes ringPulse {
  0% { box-shadow: 0 0 0 0 rgba(255,255,255,0.4); }
  70% { box-shadow: 0 0 0 20px rgba(255,255,255,0); }
  100% { box-shadow: 0 0 0 0 rgba(255,255,255,0); }
}

@keyframes popIn {
  from { transform: scale(0.5); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

.brand-reveal {
  display: flex;
  justify-content: center;
  gap: 0.25rem;
  font-size: 2.75rem;
  font-weight: 800;
  margin-bottom: 2rem;
}

.brand-petty, .brand-cash {
  display: inline-block;
  opacity: 0;
  transform: translateY(20px);
  animation: textReveal 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.brand-petty { color: white; animation-delay: 0.3s; }
.brand-cash { color: #4ADE80; animation-delay: 0.5s; }

@keyframes textReveal {
  to { opacity: 1; transform: translateY(0); }
}

.loading-track {
  width: 180px;
  height: 3px;
  background: rgba(255,255,255,0.15);
  border-radius: 2px;
  margin: 0 auto;
  overflow: hidden;
}

.loading-fill {
  height: 100%;
  width: 0;
  background: white;
  border-radius: 2px;
  animation: loadFill 1.8s ease-out forwards;
}

@keyframes loadFill {
  0% { width: 0; }
  100% { width: 100%; }
}

.fade-slow-enter-active, .fade-slow-leave-active {
  transition: opacity 0.6s ease;
}
.fade-slow-enter, .fade-slow-leave-to {
  opacity: 0;
}

/* ═══════════════════════════════════════
   AMBIENT BACKGROUND
   ═══════════════════════════════════════ */
.app-shell {
  position: relative;
  min-height: 100vh;
  background: linear-gradient(180deg, #F0F7FF 0%, #F8FAFC 40%, #FFFFFF 100%);
}

.ambient-layer {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
}

.orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.35;
  animation: orbDrift 20s ease-in-out infinite alternate;
}

.orb-blue {
  width: 500px;
  height: 500px;
  background: #00479B;
  top: -10%;
  right: -5%;
  animation-duration: 25s;
}

.orb-teal {
  width: 400px;
  height: 400px;
  background: #009688;
  bottom: 10%;
  left: -10%;
  animation-duration: 30s;
  animation-delay: -5s;
}

.orb-gold {
  width: 300px;
  height: 300px;
  background: #F97316;
  top: 40%;
  left: 40%;
  opacity: 0.15;
  animation-duration: 22s;
  animation-delay: -10s;
}

@keyframes orbDrift {
  from { transform: translate(0, 0) scale(1); }
  to { transform: translate(40px, -40px) scale(1.1); }
}

/* ═══════════════════════════════════════
   HERO
   ═══════════════════════════════════════ */
.hero-zone {
  position: relative;
  z-index: 1;
  padding: 6rem 0 3rem;
}

.hero-content {
  text-align: center;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(0, 71, 155, 0.08);
  color: var(--pc-primary);
  padding: 0.5rem 1rem;
  border-radius: 999px;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(0, 71, 155, 0.12);
}

.hero-headline {
  font-size: clamp(2.5rem, 6vw, 4.5rem);
  font-weight: 800;
  line-height: 1.05;
  color: var(--pc-text);
  letter-spacing: -0.03em;
  margin-bottom: 1.25rem;
}

.text-gradient {
  background: linear-gradient(135deg, #00479B 0%, #009688 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-lead {
  font-size: clamp(1.1rem, 2vw, 1.35rem);
  color: var(--pc-muted);
  max-width: 560px;
  margin: 0 auto 2rem;
  line-height: 1.6;
}
.text-sas {
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-ctas {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 3rem;
}

.btn-primary-glow {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #00479B, #0066CC);
  color: white;
  border: none;
  padding: 0.875rem 1.75rem;
  border-radius: 1rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(0, 71, 155, 0.35), 0 0 0 0 rgba(0, 71, 155, 0.4);
  transition: all 0.3s ease;
  animation: glowPulse 3s infinite;
}

.btn-primary-glow:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(0, 71, 155, 0.45);
}

@keyframes glowPulse {
  0%, 100% { box-shadow: 0 4px 20px rgba(0, 71, 155, 0.35), 0 0 0 0 rgba(0, 71, 155, 0.4); }
  50% { box-shadow: 0 4px 20px rgba(0, 71, 155, 0.45), 0 0 0 8px rgba(0, 71, 155, 0); }
}

.btn-ghost {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: white;
  color: var(--pc-primary);
  border: 1.5px solid var(--pc-border);
  padding: 0.875rem 1.75rem;
  border-radius: 1rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-ghost:hover {
  border-color: var(--pc-primary);
  background: rgba(0, 71, 155, 0.03);
  transform: translateY(-2px);
}

/* Hero floating stats */
.hero-float-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  max-width: 720px;
  margin: 0 auto;
}

.hstat-card {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 1rem;
  padding: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  box-shadow: var(--shadow);
  transition: transform 0.3s ease;
}

.hstat-card:hover {
  transform: translateY(-4px);
}

.hstat-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(0, 71, 155, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
}

.hstat-value {
  font-size: 1.125rem;
  font-weight: 800;
  color: var(--pc-text);
  line-height: 1;
}

.hstat-label {
  font-size: 0.75rem;
  color: var(--pc-muted);
  font-weight: 500;
  margin-top: 0.25rem;
}

/* ═══════════════════════════════════════
   SCROLL REVEAL SYSTEM
   ═══════════════════════════════════════ */
.scroll-reveal {
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1), transform 0.7s cubic-bezier(0.16, 1, 0.3, 1);
}

.scroll-reveal.is-visible {
  opacity: 1;
  transform: translateY(0);
}

.animate-enter {
  opacity: 0;
  transform: translateY(20px);
  animation: enterUp 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

@keyframes enterUp {
  to { opacity: 1; transform: translateY(0); }
}

/* ═══════════════════════════════════════
   SECTION HEADERS
   ═══════════════════════════════════════ */
.section-header {
  text-align: center;
  margin-bottom: 3rem;
}

.section-eyebrow {
  display: inline-block;
  font-size: 0.875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--pc-primary);
  background: rgba(0, 71, 155, 0.06);
  padding: 0.35rem 0.9rem;
  border-radius: 999px;
  margin-bottom: 0.75rem;
  text-decoration: none;
}

.section-title {
  font-size: clamp(1.75rem, 4vw, 2.5rem);
  font-weight: 800;
  color: var(--pc-text);
  letter-spacing: -0.02em;
  margin-bottom: 0.75rem;
}

.section-sub {
  color: var(--pc-muted);
  font-size: 1.1rem;
  max-width: 500px;
  margin: 0 auto;
  line-height: 1.6;
}

/* ═══════════════════════════════════════
   ACCOUNTS ZONE
   ═══════════════════════════════════════ */
.accounts-zone {
  position: relative;
  z-index: 1;
  padding: 5rem 0;
}

.accounts-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}

@media (min-width: 768px) {
  .accounts-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem;
  }
}

.account-card {
  position: relative;
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(16px);
  border-radius: var(--radius);
  border: 1px solid rgba(255, 255, 255, 0.6);
  box-shadow: var(--shadow);
  overflow: hidden;
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.4s ease;
}

.account-card:hover {
  transform: translateY(-6px);
  box-shadow: var(--shadow-lg), 0 0 0 1px rgba(0, 71, 155, 0.08);
}

.card-glow {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle at 50% 0%, var(--glow-color, rgba(0,71,155,0.08)), transparent 60%);
  opacity: 0;
  transition: opacity 0.4s ease;
  pointer-events: none;
}

.card-accent-blue { --glow-color: rgba(0,71,155,0.12); }
.card-accent-teal { --glow-color: rgba(0,150,136,0.12); }
.card-accent-orange { --glow-color: rgba(249,115,22,0.12); }

.account-card:hover .card-glow {
  opacity: 1;
}

.card-inner {
  position: relative;
  z-index: 2;
  padding: 1.75rem;
}

.card-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.25rem;
}

.card-icon-wrap {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bg-blue { background: linear-gradient(135deg, #00479B, #0066CC); box-shadow: 0 8px 20px rgba(0,71,155,0.25); }
.bg-teal { background: linear-gradient(135deg, #E0F2F1, #B2DFDB); }
.bg-orange { background: linear-gradient(135deg, #FFF3E0, #FFE0B2); }

.card-badge {
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
}

.badge-blue { background: #E3F2FD; color: #00479B; }
.badge-teal { background: #E0F2F1; color: #00695C; }
.badge-orange { background: #FFF3E0; color: #E65100; }

.card-title {
  font-size: 1.35rem;
  font-weight: 800;
  color: var(--pc-text);
  margin-bottom: 0.25rem;
  letter-spacing: -0.01em;
}

.card-price {
  font-size: 0.95rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
}

.text-blue { color: #00479B; }
.text-teal { color: #00796B; }
.text-orange { color: #EF6C00; }

.card-teaser {
  font-size: 0.95rem;
  color: var(--pc-muted);
  line-height: 1.6;
  margin-bottom: 1rem;
}


.feature-list {
  list-style: none;
  padding: 0.75rem;
  margin: 0 0 1rem;
  background: #F8FAFC;
  border-radius: 0.75rem;
  border: 1px solid var(--pc-border);
}

.feature-list li {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #334155;
  padding: 0.4rem 0;
}

.card-actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.card-cta {
  border-radius: 0.875rem !important;
  text-transform: none !important;
  font-weight: 700 !important;
  letter-spacing: 0.01em;
  padding: 0.75rem 0 !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.card-cta:hover {
  transform: translateX(4px);
}

.details-toggle {
  background: transparent;
  border: none;
  color: var(--pc-muted);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem;
  align-self: center;
  transition: color 0.2s;
}

.details-toggle:hover {
  color: var(--pc-primary);
}

/* Quick chips */
.quick-chips {
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 2.5rem;
  flex-wrap: wrap;
}

.quick-chip {
  font-weight: 600;
  text-transform: none !important;
  border-radius: 0.75rem !important;
  padding: 0 1rem !important;
  height: 40px !important;
  cursor: pointer;
  transition: transform 0.2s ease !important;
}

.quick-chip:hover {
  transform: translateY(-2px);
}

/* ═══════════════════════════════════════
   BENTO GRID
   ═══════════════════════════════════════ */
.bento-zone {
  position: relative;
  z-index: 1;
  padding: 5rem 0;
  background: linear-gradient(180deg, rgba(240,247,255,0.5) 0%, rgba(255,255,255,0) 100%);
}

.bento-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
}

@media (min-width: 768px) {
  .bento-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .bento-large { grid-row: span 2; }
  .bento-wide { grid-column: span 2; }
}

.bento-card {
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: var(--radius);
  padding: 1.75rem;
  box-shadow: var(--shadow);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
}

.bento-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg);
}

.bento-icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.25rem;
}

.bg-soft-primary { background: #E3F2FD; }
.bg-soft-teal { background: #E0F2F1; }
.bg-soft-purple { background: #F3E5F5; }
.bg-soft-orange { background: #FFF3E0; }

.bento-card h3 {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--pc-text);
  margin-bottom: 0.5rem;
}

.bento-card p {
  font-size: 0.95rem;
  color: var(--pc-muted);
  line-height: 1.6;
  margin-bottom: 1rem;
  flex: 1;
}

.bento-visual {
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  height: 80px;
  margin-top: auto;
}

.mock-bar {
  flex: 1;
  background: linear-gradient(to top, #00479B, #4facfe);
  border-radius: 4px 4px 0 0;
  opacity: 0.8;
  transition: height 1s ease;
  animation: barPulse 3s ease-in-out infinite alternate;
}

.mock-bar:nth-child(2n) { animation-delay: 0.5s; background: linear-gradient(to top, #009688, #4fd1c5); }
.mock-bar:nth-child(3n) { animation-delay: 1s; background: linear-gradient(to top, #F97316, #fbbf24); }

@keyframes barPulse {
  from { opacity: 0.6; transform: scaleY(0.95); }
  to { opacity: 1; transform: scaleY(1); }
}

/* ═══════════════════════════════════════
   TRUST ZONE
   ═══════════════════════════════════════ */
.trust-zone {
  position: relative;
  z-index: 1;
  padding: 4rem 0;
}

.trust-flex {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: var(--radius);
  padding: 2.5rem 1.5rem;
  box-shadow: var(--shadow);
}

@media (min-width: 768px) {
  .trust-flex {
    flex-direction: row;
    justify-content: space-around;
    padding: 2.5rem;
  }
}

.trust-block {
  text-align: center;
  flex: 1;
}

.trust-pulse {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(0, 71, 155, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  position: relative;
}

.trust-pulse::after {
  content: '';
  position: absolute;
  inset: -4px;
  border-radius: 50%;
  border: 2px solid rgba(0, 71, 155, 0.08);
  animation: trustRipple 3s infinite;
}

@keyframes trustRipple {
  0% { transform: scale(1); opacity: 1; }
  100% { transform: scale(1.4); opacity: 0; }
}

.trust-num {
  font-size: 2rem;
  font-weight: 800;
  color: var(--pc-text);
  line-height: 1;
  margin-bottom: 0.35rem;
}

.trust-label {
  font-size: 0.875rem;
  color: var(--pc-muted);
  font-weight: 500;
}

.trust-divider {
  width: 1px;
  height: 60px;
  background: var(--pc-border);
  display: none;
}

@media (min-width: 768px) {
  .trust-divider { display: block; }
}

/* ═══════════════════════════════════════
   FOOTER
   ═══════════════════════════════════════ */
.pc-footer {
  position: relative;
  z-index: 1;
  background: #0F172A;
  color: #94A3B8;
  padding: 4rem 0 2rem;
}

.footer-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2.5rem;
  margin-bottom: 3rem;
}

@media (min-width: 768px) {
  .footer-grid {
    grid-template-columns: 2fr 1fr 1fr 1fr;
    gap: 3rem;
  }
}

.footer-logo {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.5rem;
  font-weight: 800;
  color: white;
  margin-bottom: 1rem;
}

.sas-tag {
  font-size: 0.75rem;
  color: #64748B;
  font-weight: 600;
  margin-left: 0.25rem;
}

.footer-mission {
  font-size: 0.95rem;
  line-height: 1.7;
  max-width: 320px;
  margin-bottom: 1.5rem;
}

.footer-socials {
  display: flex;
  gap: 0.75rem;
}

.social-btn {
  background: rgba(255,255,255,0.06) !important;
  color: #CBD5E1 !important;
  transition: all 0.3s ease !important;
}

.social-btn:hover {
  background: rgba(255,255,255,0.12) !important;
  color: white !important;
  transform: translateY(-2px);
}

.footer-col h4 {
  color: white;
  font-size: 0.95rem;
  font-weight: 700;
  margin-bottom: 1.25rem;
  letter-spacing: 0.03em;
}

.footer-col a {
  display: block;
  color: #94A3B8;
  text-decoration: none;
  font-size: 0.9rem;
  margin-bottom: 0.75rem;
  transition: color 0.2s;
}

.footer-col a:hover {
  color: white;
}

.footer-bottom {
  border-top: 1px solid rgba(255,255,255,0.06);
  padding-top: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  font-size: 0.875rem;
}

@media (min-width: 768px) {
  .footer-bottom {
    flex-direction: row;
    justify-content: space-between;
  }
}

.footer-badges {
  display: flex;
  gap: 1rem;
}

.footer-badges span {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  background: rgba(255,255,255,0.05);
  padding: 0.4rem 0.9rem;
  border-radius: 999px;
  font-size: 0.8rem;
}

/* ═══════════════════════════════════════
   CHATBOT MODERNIZED
/* ═══════════════════════════════════════
   CHATBOT STYLING & RESPONSIVENESS
   ═══════════════════════════════════════ */

/* Floating FAB Container & Invitation Bubble */
.chat-fab-wrapper {
  position: fixed;
  bottom: 1.25rem;
  right: 1.25rem;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.6rem;
  z-index: 1000;
  pointer-events: auto;
  transition: all 0.3s ease;
}

.chat-fab-wrapper.fab-hidden {
  transform: scale(0) rotate(-20deg);
  opacity: 0;
  pointer-events: none;
}

/* Invitation Bubble */
.chat-invitation-bubble {
  background: #ffffff;
  border-radius: 1.25rem 1.25rem 0.25rem 1.25rem;
  padding: 0.85rem 1.1rem;
  max-width: 290px;
  box-shadow: 0 12px 30px -4px rgba(0, 71, 155, 0.2), 0 4px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 71, 155, 0.15);
  cursor: pointer;
  position: relative;
  transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.25s;
  animation: bubbleFloat 4s ease-in-out infinite;
}

.chat-invitation-bubble:hover {
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 16px 36px -4px rgba(0, 71, 155, 0.3);
}

@keyframes bubbleFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
}

.bubble-header {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin-bottom: 0.35rem;
}

.bubble-avatar-mini {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: linear-gradient(135deg, #00479B, #009688);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.bubble-title {
  font-size: 0.75rem;
  font-weight: 700;
  color: #00479B;
  flex: 1;
  letter-spacing: 0.02em;
}

.bubble-close-btn {
  background: transparent;
  border: none;
  font-size: 0.85rem;
  line-height: 1;
  color: #94A3B8;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 4px;
  transition: color 0.2s, background 0.2s;
}

.bubble-close-btn:hover {
  color: #0F172A;
  background: #F1F5F9;
}

.bubble-text {
  font-size: 0.88rem;
  font-weight: 600;
  color: #1E293B;
  margin: 0 0 0.15rem 0;
  line-height: 1.35;
}

.bubble-subtext {
  font-size: 0.75rem;
  color: #64748B;
  display: block;
}

/* Bubble transition */
.bubble-slide-enter-active,
.bubble-slide-leave-active {
  transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);
}
.bubble-slide-enter {
  opacity: 0;
  transform: translateY(15px) scale(0.9);
}
.bubble-slide-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.9);
}

/* FAB */
.chat-fab {
  width: 3.75rem;
  height: 3.75rem;
  border-radius: 50% !important;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.chat-fab:hover {
  transform: scale(1.08) rotate(4deg);
  box-shadow: 0 12px 40px rgba(0, 71, 155, 0.45) !important;
}

/* ═══════════════════════════════════════
   MOBILE CHAT OVERLAY & DRAWER
   ═══════════════════════════════════════ */
.chat-mobile-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 10000;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.chat-card-mobile {
  background: white;
  border-radius: 1.5rem 1.5rem 0 0;
  overflow: hidden;
  height: 85vh;
  height: 85dvh;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  width: 100%;
  box-shadow: 0 -10px 35px rgba(0, 0, 0, 0.25);
}

/* Slide up animation for mobile drawer */
.chat-slide-up-enter-active,
.chat-slide-up-leave-active {
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
.chat-slide-up-enter {
  opacity: 0;
}
.chat-slide-up-enter .chat-card-mobile {
  transform: translateY(100%);
}
.chat-slide-up-leave-to {
  opacity: 0;
}
.chat-slide-up-leave-to .chat-card-mobile {
  transform: translateY(100%);
}

.chat-close-btn {
  background: rgba(255, 255, 255, 0.18);
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
}
.chat-close-btn:active {
  background: rgba(255, 255, 255, 0.35);
}

/* Desktop Dialog */
.chat-dialog {
  border-radius: 1.5rem !important;
  overflow: hidden;
  margin: 16px !important;
  max-width: 440px !important;
}

.chat-card-desk {
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 1.5rem !important;
  overflow: hidden;
  height: 550px;
  max-height: calc(100vh - 60px);
}

.chat-header-glass {
  background: linear-gradient(135deg, #00479B, #0066CC);
  padding: 1rem 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: white;
  flex-shrink: 0;
}

.chat-identity {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.chat-avatar-pulse {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: rgba(255,255,255,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.chat-avatar-pulse::after {
  content: '';
  position: absolute;
  inset: -4px;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.3);
  animation: chatPulse 2s infinite;
}

@keyframes chatPulse {
  0% { transform: scale(1); opacity: 1; }
  100% { transform: scale(1.5); opacity: 0; }
}

.chat-name {
  font-weight: 700;
  font-size: 1rem;
  line-height: 1;
}

.chat-status {
  font-size: 0.75rem;
  opacity: 0.9;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.2rem;
}

.status-dot {
  width: 8px;
  height: 8px;
  background: #4ADE80;
  border-radius: 50%;
  display: inline-block;
  box-shadow: 0 0 0 0 rgba(74, 222, 128, 0.4);
  animation: statusPulse 2s infinite;
}

@keyframes statusPulse {
  0% { box-shadow: 0 0 0 0 rgba(74, 222, 128, 0.4); }
  70% { box-shadow: 0 0 0 6px rgba(74, 222, 128, 0); }
  100% { box-shadow: 0 0 0 0 rgba(74, 222, 128, 0); }
}

/* ═══════════════════════════════════════
   CHAT MESSAGES & TOUCH SCROLL
   ═══════════════════════════════════════ */
.chat-body-mobile,
.chat-body-desk {
  flex: 1 1 auto;
  height: 0;
  min-height: 0;
  overflow-y: auto !important;
  -webkit-overflow-scrolling: touch !important;
  touch-action: pan-y !important;
  overscroll-behavior: contain;
  padding: 1.25rem;
  background: #F8FAFC;
}

.msg-row {
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  margin-bottom: 1rem;
  animation: msgPop 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes msgPop {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.msg-user { justify-content: flex-end; }
.msg-bot { justify-content: flex-start; }

.msg-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #E3F2FD;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.msg-content-column {
  display: flex;
  flex-direction: column;
  max-width: 86%;
  min-width: 0;
}
.msg-user .msg-content-column {
  align-items: flex-end;
}
.msg-bot .msg-content-column {
  align-items: flex-start;
}

.msg-bubble {
  padding: 0.75rem 1rem;
  border-radius: 1rem;
  font-size: 0.92rem;
  line-height: 1.5;
  width: fit-content;
  max-width: 100%;
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

/* Rendered bot markdown (Groq answers use Markdown) */
.bot-md {
  white-space: normal;
}
.bot-md .chat-md-p {
  margin: 0 0 0.45rem;
}
.bot-md .chat-md-p:last-child {
  margin-bottom: 0;
}
.bot-md .chat-md-h1,
.bot-md .chat-md-h2 {
  font-size: 1rem;
  font-weight: 800;
  margin: 0.55rem 0 0.35rem;
}
.bot-md .chat-md-h3,
.bot-md .chat-md-h4 {
  font-size: 0.95rem;
  font-weight: 700;
  margin: 0.5rem 0 0.3rem;
}
.bot-md .chat-md-ul {
  margin: 0.25rem 0 0.5rem;
  padding-left: 1.15rem;
}
.bot-md .chat-md-ul li {
  margin-bottom: 0.25rem;
}
.bot-md .chat-md-li {
  margin: 0 0 0.3rem;
}
.bot-md strong {
  font-weight: 800;
}
.bot-md code.chat-code {
  background: rgba(0, 71, 155, 0.1);
  border-radius: 0.3rem;
  padding: 0.05rem 0.35rem;
  font-size: 0.85em;
}
.bot-md a {
  color: #00479B;
  text-decoration: underline;
  overflow-wrap: anywhere;
}

/* Video Suggestions Cards */
.msg-video-suggestions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;
  width: 100%;
}

.video-suggestion-card {
  background: #ffffff;
  border: 1px solid #E2E8F0;
  border-radius: 0.85rem;
  padding: 0.5rem 0.65rem;
  display: flex;
  align-items: center;
  gap: 0.65rem;
  cursor: pointer;
  box-shadow: 0 3px 10px rgba(0, 71, 155, 0.08);
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  width: 100%;
  max-width: 320px;
}

.video-suggestion-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(0, 71, 155, 0.16);
  border-color: #00479B;
}

.video-card-thumb {
  position: relative;
  width: 54px;
  height: 40px;
  border-radius: 0.5rem;
  overflow: hidden;
  flex-shrink: 0;
  background: #0F172A;
  display: flex;
  align-items: center;
  justify-content: center;
}

.thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumb-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #00479B, #009688);
}

.play-badge {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.28);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.video-suggestion-card:hover .play-badge {
  background: rgba(0, 71, 155, 0.45);
}

.video-card-info {
  flex: 1;
  min-width: 0;
}

.video-card-badge {
  font-size: 0.65rem;
  font-weight: 700;
  color: #00479B;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 2px;
}

.video-card-title {
  font-size: 0.8rem;
  font-weight: 600;
  color: #0F172A;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.video-watch-link {
  font-size: 0.72rem;
  font-weight: 700;
  color: #00479B;
  display: flex;
  align-items: center;
  gap: 3px;
  margin-top: 3px;
}

/* Video Player Modal Dialog */
.chat-video-dialog {
  overflow: hidden;
}
.chat-video-dialog:not(.v-dialog--fullscreen) {
  border-radius: 1.25rem !important;
  margin: 16px !important;
}

.bg-dark-video {
  background: #0F172A !important;
}

.video-dialog-header {
  background: #1E293B;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.modal-close-icon {
  background: rgba(255, 255, 255, 0.12);
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
}

.modal-close-icon:hover {
  background: rgba(255, 255, 255, 0.25);
}

.video-dialog-player {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #000000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-frame-modal,
.video-element-modal {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.bg-dark-caption {
  background: #1E293B;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.msg-bubble.user {
  background: linear-gradient(135deg, #00479B, #0066CC);
  color: white;
  border-bottom-right-radius: 0.25rem;
}

.msg-bubble.bot {
  background: white;
  color: var(--pc-text);
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  border-bottom-left-radius: 0.25rem;
}

.msg-bubble.typing {
  display: flex;
  gap: 0.35rem;
  padding: 0.9rem 1.1rem;
}

.t-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #CBD5E1;
  animation: typingBounce 1.4s infinite ease-in-out both;
}

.t-dot:nth-child(1) { animation-delay: 0s; }
.t-dot:nth-child(2) { animation-delay: 0.2s; }
.t-dot:nth-child(3) { animation-delay: 0.4s; }

@keyframes typingBounce {
  0%, 80%, 100% { transform: scale(0.6); }
  40% { transform: scale(1); }
}

/* ═══════════════════════════════════════
   INPUT AREA
   ═══════════════════════════════════════ */
.chat-input-area {
  padding: 0.75rem 1rem calc(0.75rem + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid var(--pc-border, #E2E8F0);
  background: white;
  flex-shrink: 0;
}

.chat-input-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #F1F5F9;
  border-radius: 999px;
  padding: 0.35rem 0.4rem 0.35rem 1rem;
  border: 1px solid #E2E8F0;
  transition: border-color 0.2s;
}

.chat-input-wrapper:focus-within {
  border-color: #00479B;
  background: #FFFFFF;
}

.chat-native-input {
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-size: 0.92rem;
  color: #0F172A;
  min-width: 0;
  padding: 0.35rem 0;
}

.chat-native-input::placeholder {
  color: #94A3B8;
  font-size: 0.85rem;
}

.chat-send-btn {
  width: 36px;
  height: 36px;
  min-width: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #00479B, #0066CC);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.2s, opacity 0.2s;
}

.chat-send-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.chat-send-btn:not(:disabled):active {
  transform: scale(0.92);
}

.chat-field {
  border-radius: 1rem !important;
  background: #F1F5F9 !important;
}

.chat-field >>> .v-input__slot {
  box-shadow: none !important;
  background: transparent !important;
  padding: 0 0.75rem !important;
}

/* ═══════════════════════════════════════
   RESPONSIVE TWEAKS
   ═══════════════════════════════════════ */
@media (max-width: 640px) {
  .hero-zone { padding: 5rem 0 2rem; }
  .hero-float-stats { grid-template-columns: 1fr; max-width: 320px; }
  .hstat-card { padding: 0.875rem 1rem; }
  .accounts-grid { gap: 1.25rem; }
  .account-card { border-radius: 1rem; }
  .card-inner { padding: 1.5rem; }
  .bento-wide { grid-column: span 1; }
  .quick-chips { gap: 0.5rem; }
  .trust-flex { gap: 1.5rem; }
  .trust-divider { display: none !important; }
  .chat-fab { width: 3.25rem; height: 3.25rem; bottom: 1rem; right: 1rem; }
}

@media (min-width: 1024px) {
  .hero-zone { padding: 7rem 0 4rem; }
  .accounts-zone { padding: 6rem 0; }
  .bento-zone { padding: 6rem 0; }
}

/* Scrollbar polish */
.chat-body-mobile::-webkit-scrollbar,
.chat-body-desk::-webkit-scrollbar {
  width: 6px;
}
.chat-body-mobile::-webkit-scrollbar-thumb,
.chat-body-desk::-webkit-scrollbar-thumb {
  background: #CBD5E1;
  border-radius: 3px;
}
.brand-hero {
  display: inline-block;
  font-weight: 800;
  letter-spacing: -0.03em;
}

.petty-blue {
  color: #00479B;
}

.cash-green {
  color: #10B981; /* Vert émeraude */
  /* ou si vous préférez le vert Vuetify exact : #009688 */
}

.sas-gold {
  color: #D4AF37;
  font-size: 0.55em;
  font-weight: 700;

  margin-left: 0.15em;
  letter-spacing: 0.02em;
}
.card-details {
  overflow: hidden;
}
</style>

<style>
/* Global body lock when mobile chat is open */
body.chat-open-locked {
  overflow: hidden !important;
  touch-action: none;
}

/* Video player content always in front (mobile chat overlay / FAB use very high z-indexes).
   NOTE: do NOT raise .v-overlay here — the hidden video dialog stays in the DOM,
   so a body:has() rule would also lift the CHAT's own scrim above the chat. */
.chat-video-dialog {
  z-index: 20001 !important;
}
</style>